import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  type UploadItem,
  groupUploadsByProject,
  summarizeUploads,
  uploadPercent,
} from '../lib/upload-items.js'
import { useUploadActions, useUploadState } from '../lib/upload-manager.js'

/**
 * アップロードの進捗パネル。画面右下に常駐し、どの画面から上げた分もまとめて出す
 * （Google ドライブと同じ）。サイドバーから別のプロジェクトへ上げた場合も、
 * 今開いている画面を離れずに進み具合を確認・中断できる。
 */
export function UploadPanel() {
  const { items } = useUploadState()
  const { dismiss, clearFinished } = useUploadActions()
  const [collapsed, setCollapsed] = useState(false)

  if (items.length === 0) return null

  const summary = summarizeUploads(items)
  const groups = groupUploadsByProject(items)

  return (
    <section
      aria-label="アップロードの進捗"
      className="fixed bottom-6 right-6 z-40 w-[22rem] max-w-[calc(100vw-3rem)] overflow-hidden rounded-lg border border-border bg-card shadow-xl"
    >
      <header className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-2">
        <p aria-live="polite" className="flex-1 truncate text-xs font-medium">
          {summaryText(summary)}
        </p>
        {summary.hasFinished && (
          <button
            type="button"
            onClick={clearFinished}
            className="shrink-0 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
          >
            完了済を消す
          </button>
        )}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? '進捗を開く' : '進捗を閉じる'}
          className="shrink-0 grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <span
            aria-hidden="true"
            className={`transition-transform duration-150 ${collapsed ? 'rotate-180' : ''}`}
          >
            ▾
          </span>
        </button>
      </header>

      {!collapsed && (
        <div className="max-h-[50vh] overflow-y-auto p-2 space-y-3">
          {groups.map((group) => (
            <div key={group.projectId}>
              <Link
                to={`/projects/${group.projectId}`}
                className="block truncate px-1 pb-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground hover:underline"
              >
                {group.projectName}
              </Link>
              <ul className="space-y-1.5">
                {group.items.map((it) => (
                  <UploadRow key={it.id} item={it} onDismiss={() => dismiss(it.id)} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function summaryText(summary: ReturnType<typeof summarizeUploads>): string {
  if (summary.pending === 0 && summary.errored === 0) return 'アップロードが完了しました'
  const parts = [`アップロード中 ${summary.uploading} 件`]
  if (summary.queued > 0) parts.push(`待機中 ${summary.queued} 件`)
  if (summary.errored > 0) parts.push(`失敗 ${summary.errored} 件`)
  return parts.join(' · ')
}

function UploadRow({ item, onDismiss }: { item: UploadItem; onDismiss: () => void }) {
  const pct = uploadPercent(item)
  const label = dismissLabel(item.status)
  const barTone = progressBarTone(item.status)

  return (
    <li className={`rounded-md border px-2.5 py-2 transition-colors ${rowTone(item.status)}`}>
      <div className="flex items-center gap-2">
        <StatusMark status={item.status} />
        <span className="min-w-0 flex-1 truncate text-xs font-medium" title={item.file.name}>
          {item.file.name}
        </span>
        <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground">
          <StatusText item={item} pct={pct} />
        </span>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={label}
          title={label}
          className="shrink-0 px-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ✕
        </button>
      </div>
      {barTone && (
        <div className="mt-1.5 h-1.5 overflow-hidden rounded bg-muted">
          <div className={`h-full transition-all ${barTone}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {item.status === 'error' && (
        <p className="mt-1.5 break-all text-[11px] text-red-600">{item.error}</p>
      )}
    </li>
  )
}

function StatusText({ item, pct }: { item: UploadItem; pct: number }) {
  switch (item.status) {
    case 'done':
      return <span className="font-medium text-green-700">完了</span>
    case 'error':
      return <span className="font-medium text-red-700">失敗</span>
    case 'cancelled':
      return <span className="font-medium text-amber-700">中断しました</span>
    case 'queued':
      return <>待機中</>
    default:
      return <>{`${formatBytes(item.loadedBytes)} / ${formatBytes(item.totalBytes)} (${pct}%)`}</>
  }
}

function dismissLabel(status: UploadItem['status']): string {
  if (status === 'uploading') return 'アップロードを中断'
  if (status === 'queued') return '待機をキャンセル'
  return '閉じる'
}

/** 進捗バーの色。待機中と完了はバーを出さないので null */
function progressBarTone(status: UploadItem['status']): string | null {
  switch (status) {
    case 'uploading':
      return 'bg-primary'
    case 'error':
      return 'bg-red-500'
    case 'cancelled':
      return 'bg-amber-400'
    default:
      return null
  }
}

function StatusMark({ status }: { status: UploadItem['status'] }) {
  const base =
    'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] text-white'
  if (status === 'done') {
    return (
      <span aria-label="完了" className={`${base} bg-green-500`}>
        ✓
      </span>
    )
  }
  if (status === 'error') {
    return (
      <span aria-label="失敗" className={`${base} bg-red-500`}>
        !
      </span>
    )
  }
  if (status === 'cancelled') {
    return (
      <span aria-label="中断" className={`${base} bg-amber-500`}>
        –
      </span>
    )
  }
  return null
}

function rowTone(status: UploadItem['status']): string {
  switch (status) {
    case 'done':
      return 'border-green-300 bg-green-50/40'
    case 'error':
      return 'border-red-300 bg-red-50/40'
    case 'cancelled':
      return 'border-amber-300 bg-amber-50/40'
    case 'queued':
      return 'border-muted bg-muted/30'
    default:
      return 'border-border bg-background'
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
